import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, DollarSign, RefreshCw, XCircle } from 'lucide-react';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import { damageNoteService } from '../../services/damageNoteService';
import { DamageNoteListDTO, DamageNoteStatus } from '../../types';
import './DamageNotePage.css';

const HISTORY_STATUSES: DamageNoteStatus[] = ['Posted', 'Cancelled'];

const STATUS_CLASS: Record<DamageNoteStatus, string> = {
  Draft:           'ims-badge dn-badge-draft',
  PendingApproval: 'ims-badge dn-badge-pending',
  Approved:        'ims-badge dn-badge-approved',
  Rejected:        'ims-badge dn-badge-rejected',
  Posted:          'ims-badge dn-badge-posted',
  Cancelled:       'ims-badge dn-badge-cancelled',
};

const STATUS_LABEL: Record<DamageNoteStatus, string> = {
  Draft:           'Draft',
  PendingApproval: 'Pending Approval',
  Approved:        'Approved',
  Rejected:        'Rejected',
  Posted:          'Posted',
  Cancelled:       'Cancelled',
};

const DamageNoteHistoryPage: React.FC = () => {
  const navigate  = useNavigate();
  const { t }     = useTranslation();

  const [items,   setItems]   = useState<DamageNoteListDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res     = await damageNoteService.getAll(1, 500);
      const payload = res.data?.data;
      const all: DamageNoteListDTO[] = (payload as any)?.data ?? (payload as any) ?? [];
      setItems(all.filter(d => HISTORY_STATUSES.includes(d.status)));
    } catch {
      setError(t('damageNote.failedToLoad', 'Failed to load damage notes.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const postedCount    = items.filter(d => d.status === 'Posted').length;
  const cancelledCount = items.filter(d => d.status === 'Cancelled').length;
  const totalPostedLoss = items
    .filter(d => d.status === 'Posted')
    .reduce((sum, d) => sum + d.totalEstimatedValue, 0);

  const columns: GridColumn[] = [
    {
      key: 'damageNoteNumber',
      label: t('damageNote.damageNoteNumber', 'DN Number'),
      sortable: true,
      width: '160px',
    },
    {
      key: 'damageDate',
      label: t('damageNote.damageDate', 'Damage Date'),
      width: '120px',
      render: (v) => new Date(v as string).toLocaleDateString(),
    },
    {
      key: 'warehouseName',
      label: t('damageNote.warehouse', 'Warehouse'),
      width: '160px',
    },
    {
      key: 'status',
      label: t('common.status', 'Status'),
      width: '130px',
      render: (v) => {
        const s = v as DamageNoteStatus;
        return (
          <span className={STATUS_CLASS[s] ?? 'ims-badge'}>
            {STATUS_LABEL[s] ?? s}
          </span>
        );
      },
    },
    {
      key: 'itemCount',
      label: t('damageNote.items', 'Items'),
      width: '80px',
    },
    {
      key: 'totalEstimatedValue',
      label: t('damageNote.estimatedValue', 'Est. Value'),
      width: '140px',
      render: (v) => (v as number).toFixed(2),
    },
    {
      key: 'reportedByName',
      label: t('damageNote.reportedBy', 'Reported By'),
      width: '140px',
    },
    {
      key: 'createdAt',
      label: t('common.createdAt', 'Created'),
      width: '120px',
      render: (v) => new Date(v as string).toLocaleDateString(),
    },
  ];

  const actions: GridAction[] = [
    {
      label: t('common.view', 'View'),
      onClick: (row) => navigate(`/damage-notes/${row.id}`),
      className: 'grid-action-btn-view',
    },
  ];

  return (
    <Layout>
      <div className="ims-page dn-page">

        {/* Header */}
        <div className="ims-list-header">
          <div>
            <h1 className="ims-list-title">
              {t('damageNote.history', 'Damage Note History')}
            </h1>
            <p style={{ color: 'var(--ims-text-sub)', fontSize: 13, marginTop: 2 }}>
              {t('damageNote.historyDesc', 'Completed (posted) and cancelled damage notes')}
            </p>
          </div>
          <div className="ims-list-actions">
            <button className="btn-refresh" onClick={loadData} disabled={loading}>
              <RefreshCw
                size={14}
                strokeWidth={2}
                className={loading ? 'refresh-icon-spinning' : ''}
              />
              {loading
                ? t('common.refreshing', 'Refreshing…')
                : t('common.refresh', 'Refresh')}
            </button>
            <button
              className="ims-btn ims-btn-secondary"
              onClick={() => navigate('/damage-notes')}
            >
              {t('damageNote.backToList', 'Back to List')}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="ims-stats-row">
          <div className="ims-stat-card">
            <div className="ims-stat-icon dn-stat-icon-posted">
              <CheckCircle2 size={22} strokeWidth={1.75} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{postedCount}</span>
              <span className="ims-stat-label">{t('damageNote.posted', 'Posted')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon dn-stat-icon-total">
              <XCircle size={22} strokeWidth={1.75} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{cancelledCount}</span>
              <span className="ims-stat-label">{t('damageNote.cancelled', 'Cancelled')}</span>
            </div>
          </div>
          <div className="ims-stat-card">
            <div className="ims-stat-icon dn-stat-icon-loss">
              <DollarSign size={22} strokeWidth={1.75} />
            </div>
            <div className="ims-stat-info">
              <span className="ims-stat-value">{totalPostedLoss.toFixed(2)}</span>
              <span className="ims-stat-label">
                {t('damageNote.totalPostedLoss', 'Total Posted Loss')}
              </span>
            </div>
          </div>
        </div>

        {error && <div className="ims-alert ims-alert-error">{error}</div>}

        <div className="ims-grid-wrapper">
          <DataGrid
            columns={columns}
            data={items}
            loading={loading}
            actions={actions}
            emptyMessage={t('damageNote.noHistory', 'No posted or cancelled damage notes found')}
          />
        </div>
      </div>
    </Layout>
  );
};

export default DamageNoteHistoryPage;
